import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchWaveBreaks,
  createWaveBreaks,
  updateWaveBreaks,
  deleteWaveBreaks,
} from '@/api/waveBreaks'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type {
  CreateWaveBreaksRequest,
  UpdateWaveBreaksRequest,
} from '@/components/PlanDetail/PlanWavesDrawer/types'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

export function useWaveBreaks(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.waveBreaks.byLine(
      planId ?? '',
      versionId ?? '',
      lineId ?? ''
    ),
    queryFn: () => fetchWaveBreaks(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useCreateWaveBreaks(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: CreateWaveBreaksRequest
    }) => createWaveBreaks(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.waveBreaks.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      if (variables.body.apply_to_all_line_items) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.waveBreaks.allForPlan(planId, versionId),
        })
      }
      toast.success('Wave break model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useUpdateWaveBreaks(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: UpdateWaveBreaksRequest
    }) => updateWaveBreaks(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.waveBreaks.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      if (variables.body.apply_to_all_line_items) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.waveBreaks.allForPlan(planId, versionId),
        })
      }
      toast.success('Wave break model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeleteWaveBreaks(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lineId }: { lineId: string }) =>
      deleteWaveBreaks(planId, versionId, lineId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.waveBreaks.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Wave break model removed successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
